import type { ConditionAnalysisSliceKind } from "./conditionAnalysisSelectors";

/** v1 flat list (legacy); migrated once into v2. */
export const REPORT_PICK_LIST_STORAGE_KEY_V1 = "report_picks_v1";
/** v2: picks grouped by report id (or special buckets). */
export const REPORT_PICK_LIST_STORAGE_KEY = "report_picks_v2";

export const REPORT_PICK_LIST_CHANGED_EVENT = "reportPickListChanged";

/** Picks with no `reportId` (older data or non–report flows). */
export const UNSCOPED_PICK_BUCKET = "__unscoped__";

/** Pick meta counter: how many combined bundles used this pick as an atomic child. */
export const PICK_META_USED_AS_CHILD_COUNT_KEY = "used_as_child_count";
/** Pick meta counter: how many times this pick was run as a standalone (separate) run. */
export const PICK_META_RAN_SEPARATELY_COUNT_KEY = "ran_separately_count";

function readMetaCount(meta: Record<string, unknown> | undefined, key: string): number {
  if (!meta || typeof meta !== "object") return 0;
  const raw = meta[key];
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.max(0, raw);
  return 0;
}

/** Used in combined = atomic child count (increments when a combined bundle is created). */
export function resolvePickUsedInCombinedCount(pick: Pick<ReportPick, "meta">): number {
  return readMetaCount(pick.meta, PICK_META_USED_AS_CHILD_COUNT_KEY);
}

/** Ran separately = separate-run count (increments when “run picks separately”). */
export function resolvePickRanSeparatelyCount(pick: Pick<ReportPick, "meta">): number {
  return readMetaCount(pick.meta, PICK_META_RAN_SEPARATELY_COUNT_KEY);
}

export function incrementPickUsedInCombinedCount(
  reportId: string,
  pickId: string,
  delta: number = 1
): void {
  const bucket = reportId.trim();
  if (!bucket) return;
  const store = loadStore();
  const cur = store.byReport[bucket];
  if (!cur?.length) return;
  let changed = false;
  const next = cur.map((p) => {
    if (p.id !== pickId) return p;
    const meta = (p.meta && typeof p.meta === "object" && !Array.isArray(p.meta) ? p.meta : {}) as Record<
      string,
      unknown
    >;
    const curCount = readMetaCount(meta, PICK_META_USED_AS_CHILD_COUNT_KEY);
    const newCount = Math.max(0, curCount + delta);
    changed = changed || newCount !== curCount;
    return {
      ...p,
      meta: {
        ...meta,
        [PICK_META_USED_AS_CHILD_COUNT_KEY]: newCount,
      },
    };
  });
  if (!changed) return;
  store.byReport[bucket] = next;
  writeStore(store);
}

export function incrementPickRanSeparatelyCount(
  reportId: string,
  pickId: string,
  delta: number = 1
): void {
  const bucket = reportId.trim();
  if (!bucket) return;
  const store = loadStore();
  const cur = store.byReport[bucket];
  if (!cur?.length) return;
  let changed = false;
  const next = cur.map((p) => {
    if (p.id !== pickId) return p;
    const meta = (p.meta && typeof p.meta === "object" && !Array.isArray(p.meta) ? p.meta : {}) as Record<
      string,
      unknown
    >;
    const curCount = readMetaCount(meta, PICK_META_RAN_SEPARATELY_COUNT_KEY);
    const newCount = Math.max(0, curCount + delta);
    changed = changed || newCount !== curCount;
    return {
      ...p,
      meta: {
        ...meta,
        [PICK_META_RAN_SEPARATELY_COUNT_KEY]: newCount,
      },
    };
  });
  if (!changed) return;
  store.byReport[bucket] = next;
  writeStore(store);
}

export type PickRuleLeaf = {
  path: string;
  op: "eq" | "ne" | "in" | "gte" | "lte";
  valueNumber?: number;
  valueEnum?: string;
  valueEnumSet?: string[];
};

export type ReportPickKind =
  | "direct_rule"
  | "bucket"
  | "situation"
  | "slice"
  | "segment"
  | "exit_placement"
  | "note";

/** Snapshot of the condition-analysis slice active when the pick was saved (for simulator AND-scoping). */
export type ReportPickSliceContext = {
  sliceId: string;
  kind: ConditionAnalysisSliceKind;
  label: string;
  market_condition?: string;
  candle_direction?: string;
  trade_direction?: "long" | "short";
  /**
   * For triple-key slices (`regime_direction_trade_direction`), whether candle direction uses the long window
   * or ~120-bar short window (inferred from slice id when omitted).
   */
  triple_candle_window?: "long" | "short";
  /** For `session_time` slice context / picks: catalog token for `session_time.session_id`. */
  session_id?: string;
};

/** Org lanes chosen when adding a pick. */
export type SimulatorPickOrgLane = "single" | "combined";

/**
 * Simulator picks kanban lane.
 * `"ran"` = already run as a separate (single) simulation.
 * Combined-only usage keeps the pick in `"single"` (Available Single Picks) and
 * increments `used_as_child_count` instead of moving to `"ran"`.
 */
export type SimulatorPickLane = SimulatorPickOrgLane | "ran";

export type ReportPick = {
  v: 1;
  id: string;
  createdAtMs: number;
  kind: ReportPickKind;
  label: string;
  /** Report this pick belongs to (used for storage + simulator scoping). */
  reportId?: string;
  sliceId?: string;
  sliceContext?: ReportPickSliceContext;
  /** If convertible, a direct simulator rule. */
  rule?: PickRuleLeaf;
  /** If convertible, a machine-readable policy node list from backend suggestions. */
  policyNodes?: unknown[];
  /**
   * Kanban lane on the report Simulator tab.
   * Missing / unknown values are treated as `"single"` (legacy picks).
   */
  simulatorLane?: SimulatorPickLane;
  /** Extra data for future mapping/resolution. */
  meta?: Record<string, unknown>;
};

type PickStoreV2 = {
  v: 2;
  byReport: Record<string, ReportPick[]>;
};

type PickListPayloadV1 = {
  v: 1;
  picks: ReportPick[];
};

function safeJsonParse(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizePick(raw: unknown): ReportPick | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1) return null;
  if (typeof o.id !== "string" || !o.id.trim()) return null;
  if (typeof o.createdAtMs !== "number" || !Number.isFinite(o.createdAtMs)) return null;
  if (typeof o.kind !== "string") return null;
  if (typeof o.label !== "string" || !o.label.trim()) return null;

  const ruleRaw = o.rule;
  let rule: PickRuleLeaf | undefined;
  if (ruleRaw && typeof ruleRaw === "object" && !Array.isArray(ruleRaw)) {
    const r = ruleRaw as Record<string, unknown>;
    const path = typeof r.path === "string" ? r.path : "";
    const op = typeof r.op === "string" ? r.op : "";
    if (path && (op === "eq" || op === "ne" || op === "in" || op === "gte" || op === "lte")) {
      rule = {
        path,
        op,
        ...(typeof r.valueNumber === "number" && Number.isFinite(r.valueNumber) ? { valueNumber: r.valueNumber } : {}),
        ...(typeof r.valueEnum === "string" ? { valueEnum: r.valueEnum } : {}),
        ...(Array.isArray(r.valueEnumSet)
          ? { valueEnumSet: r.valueEnumSet.filter((x) => typeof x === "string") as string[] }
          : {}),
      };
    }
  }

  const policyNodesRaw = o.policyNodes;
  const policyNodes =
    Array.isArray(policyNodesRaw) && policyNodesRaw.length ? policyNodesRaw : undefined;

  const sliceCtxRaw = o.sliceContext;
  let sliceContext: ReportPickSliceContext | undefined;
  if (sliceCtxRaw && typeof sliceCtxRaw === "object" && !Array.isArray(sliceCtxRaw)) {
    const sc = sliceCtxRaw as Record<string, unknown>;
    const sliceId = typeof sc.sliceId === "string" ? sc.sliceId : "";
    const kind = typeof sc.kind === "string" ? sc.kind : "";
    const label = typeof sc.label === "string" ? sc.label : "";
    const validKinds = new Set([
      "all",
      "direction",
      "candle_direction",
      "candle_direction_short",
      "market_direction",
      "market_direction_short",
      "regime",
      "regime_direction",
      "regime_direction_trade_direction",
      "candle_direction_trade_direction",
      "candle_direction_short_trade_direction",
      "session_time",
      "session_trade_direction",
      "session_candle_direction",
      "session_candle_direction_short",
      "session_regime",
      "session_regime_trade_direction",
      "session_regime_direction_trade_direction",
      "session_regime_direction_short_trade_direction",
    ]);
    if (sliceId && label && validKinds.has(kind)) {
      sliceContext = {
        sliceId,
        kind: kind as ConditionAnalysisSliceKind,
        label,
        ...(typeof sc.market_condition === "string" ? { market_condition: sc.market_condition } : {}),
        ...(typeof sc.candle_direction === "string" ? { candle_direction: sc.candle_direction } : {}),
        ...(sc.trade_direction === "long" || sc.trade_direction === "short"
          ? { trade_direction: sc.trade_direction }
          : {}),
        ...(sc.triple_candle_window === "long" || sc.triple_candle_window === "short"
          ? { triple_candle_window: sc.triple_candle_window }
          : {}),
        ...(typeof sc.session_id === "string" && sc.session_id.trim() ? { session_id: sc.session_id.trim() } : {}),
      };
    }
  }

  const simulatorLane: SimulatorPickLane | undefined =
    o.simulatorLane === "combined" || o.simulatorLane === "single" || o.simulatorLane === "ran"
      ? o.simulatorLane
      : undefined;

  const out: ReportPick = {
    v: 1,
    id: o.id,
    createdAtMs: o.createdAtMs,
    kind: o.kind as ReportPickKind,
    label: o.label,
    ...(typeof o.reportId === "string" ? { reportId: o.reportId } : {}),
    ...(typeof o.sliceId === "string" ? { sliceId: o.sliceId } : {}),
    ...(sliceContext ? { sliceContext } : {}),
    ...(rule ? { rule } : {}),
    ...(policyNodes ? { policyNodes } : {}),
    ...(simulatorLane ? { simulatorLane } : {}),
    ...(o.meta && typeof o.meta === "object" && !Array.isArray(o.meta) ? { meta: o.meta as Record<string, unknown> } : {}),
  };
  return out;
}

function sortPicksNewestFirst(picks: ReportPick[]): void {
  picks.sort((a, b) => b.createdAtMs - a.createdAtMs);
}

function normalizeByReport(raw: unknown): Record<string, ReportPick[]> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const o = raw as Record<string, unknown>;
  const out: Record<string, ReportPick[]> = {};
  for (const [k, v] of Object.entries(o)) {
    if (!Array.isArray(v)) continue;
    const picks = v.map(normalizePick).filter((x): x is ReportPick => Boolean(x));
    if (picks.length) {
      sortPicksNewestFirst(picks);
      out[k] = picks;
    }
  }
  return out;
}

function migrateV1ToV2(parsed: PickListPayloadV1): PickStoreV2 {
  const byReport: Record<string, ReportPick[]> = {};
  const picksRaw = parsed.picks;
  if (!Array.isArray(picksRaw)) return { v: 2, byReport: {} };
  for (const raw of picksRaw) {
    const p = normalizePick(raw);
    if (!p) continue;
    const key = p.reportId?.trim() || UNSCOPED_PICK_BUCKET;
    if (!byReport[key]) byReport[key] = [];
    byReport[key].push(p);
  }
  for (const k of Object.keys(byReport)) sortPicksNewestFirst(byReport[k]);
  return { v: 2, byReport };
}

function readStoreV2FromDisk(): PickStoreV2 | null {
  const parsed = safeJsonParse(localStorage.getItem(REPORT_PICK_LIST_STORAGE_KEY));
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const o = parsed as Record<string, unknown>;
  if (o.v !== 2) return null;
  const br = o.byReport;
  if (!br || typeof br !== "object" || Array.isArray(br)) return null;
  return { v: 2, byReport: normalizeByReport(br) };
}

function loadStore(): PickStoreV2 {
  const existing = readStoreV2FromDisk();
  if (existing) return existing;

  const v1Raw = localStorage.getItem(REPORT_PICK_LIST_STORAGE_KEY_V1);
  if (v1Raw) {
    const parsed = safeJsonParse(v1Raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const o = parsed as Record<string, unknown>;
      if (o.v === 1 && Array.isArray(o.picks)) {
        const migrated = migrateV1ToV2(o as PickListPayloadV1);
        writeStore(migrated);
        try {
          localStorage.removeItem(REPORT_PICK_LIST_STORAGE_KEY_V1);
        } catch {
          // ignore
        }
        return migrated;
      }
    }
  }

  return { v: 2, byReport: {} };
}

function writeStore(store: PickStoreV2): void {
  try {
    localStorage.setItem(REPORT_PICK_LIST_STORAGE_KEY, JSON.stringify(store));
    window.dispatchEvent(new CustomEvent(REPORT_PICK_LIST_CHANGED_EVENT));
  } catch {
    // no-op
  }
}

/** Bucket id used in storage for this pick. */
export function pickBucketId(pick: ReportPick): string {
  return pick.reportId?.trim() || UNSCOPED_PICK_BUCKET;
}

export function readPickListForReport(reportId: string): ReportPick[] {
  const id = reportId.trim();
  if (!id) return [];
  const store = loadStore();
  const list = store.byReport[id] ?? [];
  return [...list];
}

/** All picks across reports (simulator when no `report_id` query). */
export function readAllPicksFlat(): ReportPick[] {
  const store = loadStore();
  const out: ReportPick[] = [];
  for (const arr of Object.values(store.byReport)) out.push(...arr);
  sortPicksNewestFirst(out);
  return out;
}

/** @deprecated Prefer readPickListForReport / readAllPicksFlat; kept for older call sites. */
export function readPickList(): ReportPick[] {
  return readAllPicksFlat();
}

export function addPick(pick: ReportPick): { ok: true } | { ok: false; reason: string } {
  const bucket = pickBucketId(pick);
  const store = loadStore();
  const cur = store.byReport[bucket] ? [...store.byReport[bucket]] : [];
  if (cur.some((p) => p.id === pick.id)) return { ok: false, reason: "Already in picks." };
  store.byReport[bucket] = [pick, ...cur];
  writeStore(store);
  return { ok: true };
}

export function updatePick(reportId: string, pickId: string, patch: Partial<ReportPick>): void {
  const bucket = reportId.trim() || UNSCOPED_PICK_BUCKET;
  const store = loadStore();
  const cur = store.byReport[bucket];
  if (!cur) return;
  let changed = false;
  const next = cur.map((p) => {
    if (p.id !== pickId) return p;
    changed = true;
    return { ...p, ...patch, id: p.id, v: p.v, createdAtMs: p.createdAtMs, kind: p.kind };
  });
  if (!changed) return;
  store.byReport[bucket] = next;
  writeStore(store);
}

export function getSimulatorLane(pick: Pick<ReportPick, "simulatorLane">): SimulatorPickLane {
  if (pick.simulatorLane === "ran") return "ran";
  if (pick.simulatorLane === "combined") return "combined";
  return "single";
}

/** True when the pick is still in Single/Combined (not yet run). */
export function isSimulatorPickPending(pick: Pick<ReportPick, "simulatorLane">): boolean {
  return getSimulatorLane(pick) !== "ran";
}

export function setPickSimulatorLane(
  reportId: string,
  pickId: string,
  lane: SimulatorPickLane
): void {
  updatePick(reportId, pickId, { simulatorLane: lane });
}

/** Move picks into the Ran lane after a single or combined simulation starts. */
export function markPicksAsRan(picks: Iterable<ReportPick>): void {
  const list = Array.from(picks);
  if (list.length === 0) return;

  const idsByBucket = new Map<string, Set<string>>();
  for (const pick of list) {
    const bucket = pickBucketId(pick);
    let set = idsByBucket.get(bucket);
    if (!set) {
      set = new Set();
      idsByBucket.set(bucket, set);
    }
    set.add(pick.id);
  }

  const store = loadStore();
  let changed = false;
  idsByBucket.forEach((ids, bucket) => {
    const cur = store.byReport[bucket];
    if (!cur?.length) return;
    const next = cur.map((pick) => {
      if (!ids.has(pick.id) || pick.simulatorLane === "ran") return pick;
      changed = true;
      return { ...pick, simulatorLane: "ran" as const };
    });
    store.byReport[bucket] = next;
  });
  if (changed) writeStore(store);
}

export function removePick(reportId: string, pickId: string): void {
  const bucket = reportId.trim() || UNSCOPED_PICK_BUCKET;
  const store = loadStore();
  const cur = store.byReport[bucket];
  if (!cur) return;
  const next = cur.filter((p) => p.id !== pickId);
  if (next.length === 0) {
    delete store.byReport[bucket];
  } else {
    store.byReport[bucket] = next;
  }
  writeStore(store);
}

/** Remove a pick by id by scanning all buckets (ids should be unique after report-scoped buildPickId). */
export function removePickByIdAnywhere(pickId: string): void {
  const store = loadStore();
  let changed = false;
  for (const k of Object.keys(store.byReport)) {
    const next = store.byReport[k].filter((p) => p.id !== pickId);
    if (next.length !== store.byReport[k].length) {
      changed = true;
      if (next.length === 0) delete store.byReport[k];
      else store.byReport[k] = next;
    }
  }
  if (changed) writeStore(store);
}

export function clearPicksForReport(reportId: string): void {
  const bucket = reportId.trim();
  if (!bucket) return;
  const store = loadStore();
  if (store.byReport[bucket]) {
    delete store.byReport[bucket];
    writeStore(store);
  }
}

export function clearAllPicks(): void {
  writeStore({ v: 2, byReport: {} });
}

export function pickListSizeForReport(reportId: string): number {
  return readPickListForReport(reportId).length;
}

export function pickListSize(): number {
  return readAllPicksFlat().length;
}

function stableHashString(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

export function buildPickId(
  kind: ReportPickKind,
  label: string,
  meta: Record<string, unknown> | undefined,
  reportId?: string
): string {
  const base = JSON.stringify({
    kind,
    label: label.trim(),
    meta: meta ?? {},
    reportId: reportId?.trim() ?? "",
  });
  return `pick_${stableHashString(base)}`;
}
